import React, {useEffect, useState} from 'react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card';
import {Eye, EyeOff, Loader2} from 'lucide-react';
import {toast} from 'sonner';
import {carrierSecretKey, formatTitle, setLocalStorage} from "@/helpers/helperFunctions.js";
import CryptoJS from "crypto-js";
import constants from "@/constants/constants.js";
import backendApis from "@/services/backendApis.js";
import {useKayAuth} from "@/context/AuthContext.js";
import {Skeleton} from "@/components/ui/index.js";

type SimpleConnectionCreationProps = {
  cancel: () => void;
  carrier: any;
  updateCarrierStatus: (carrier: any) => void;
};

const usernameField = 'username';
const passwordField = 'password';

const SimpleConnectionCreation = ({cancel, carrier, updateCarrierStatus}: SimpleConnectionCreationProps) => {
  const {organization, user} = useKayAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    password: '',
    username: ''
  });
  const [formShape, setFormShape] = useState(null);

  const userId = user?.user_id;

  const allCarrierAccess = user?.carrier_access || [];
  const orgCarrier = allCarrierAccess.find(c => c.carrier_name === carrier.carrier_name);

  const carrierName = carrier.carrier_name;

  const encryptionKey = import.meta.env.VITE_SECRET_ENCRYPTION_KEY || "SAMPLE_KEY";

  const encrypt = (text) => CryptoJS.AES.encrypt(text, encryptionKey).toString();

  const saveSecret = async (carrierName, credentials) => {
    const storeLocal = orgCarrier?.credential_storage === constants.localFlag;

    if (storeLocal) {
      const encryptedData = encrypt(JSON.stringify(credentials));
      const carrierKey = carrierSecretKey(carrierName);
      await setLocalStorage(carrierKey, encryptedData);
    }
    return await backendApis.createSecret({
      credentials,
      credentials_name: carrierName,
      user_id: userId
    });
  };

  const getShapes = async () => {
    try {
      const params = {carrier_name: carrierName}
      const res = await backendApis.agentCredentialsShape(params);
      if (res.err) {
        throw new Error(res.err)
      }
      const shape = res.data.shape;
      setFormShape(shape);
    } catch (e) {
      console.error(e)
    }
  }

  const handleConnect = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await saveSecret(carrierName, credentials);
      if (res?.err) {
        toast.error('Something went wrong. Please try again later.');
        return;
      }
      toast.success("Successfully connected to carrier");
      setIsLoading(false);
      setTimeout(() => {
        updateCarrierStatus(carrier);
        cancel();
      }, 1500);
    } catch {
      toast.error("Connection failed");
    } finally {
      setIsLoading(false);
    }
  };

  const init = async () => {
    await getShapes();
  }

  useEffect(() => {
    init();
  }, []);

  const fields = Object.keys(formShape || {});
  const usernameFieldExists = fields.includes(usernameField);
  const passwordFieldExists = fields.includes(passwordField);
  const otherFields = fields.filter((field) => field !== usernameField && field !== passwordField);

  return (
    <Card className="w-full max-w-lg bg-white">
      <CardHeader>
        <CardTitle>Connect to {formatTitle(carrier.carrier_name)}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleConnect}>
          {
            usernameFieldExists && (
              <div>
                <label className="text-sm font-medium">Username</label>
                <Input
                  className="mt-1"
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  placeholder="Enter username"
                  required
                  value={credentials.username}
                />
              </div>
            )
          }

          {
            passwordFieldExists && (
              <div>
                <label className="text-sm font-medium">Password</label>
                <div className="relative mt-1">
                  <Input
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                    placeholder="Enter password"
                    required
                    type={showPassword ? "text" : "password"}
                    value={credentials.password}
                  />
                  <Button
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                  </Button>
                </div>
              </div>
            )
          }

          {
            otherFields.map((field) => {
              const shape = formShape[field];
              const fieldTitle = formatTitle(field);
              return (
                <div key={field}>
                  <label className="text-sm font-medium">{fieldTitle}</label>
                  <Input
                    className="mt-1"
                    onChange={(e) => setCredentials({...credentials, [field]: e.target.value})}
                    placeholder={`Enter ${fieldTitle}`}
                    required
                    type={shape.type || "text"}
                    value={credentials[field]}
                  />
                </div>
              );
            })
          }

          {
            formShape ? null : (
              <div className='flex flex-col gap-4'>
                <Skeleton className='h-6 w-3/4 bg-gray-300' />
                <Skeleton className='h-6 w-3/4 bg-gray-300' />
                <Skeleton className='h-6 w-3/4 bg-gray-300' />
              </div>
            )
          }

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              disabled={isLoading}
              onClick={cancel}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isLoading}
              type="submit"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
              Save Credentials
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default SimpleConnectionCreation;
